import { getCurrentTime } from "./generalUtils"
import { getNursePatients } from "../features/schedule/derivedAssignments"
import {
	rangesOverlap,
	getStartTime,
	getEndTime,
	confirmedCancellationNoShow,
	currentlyScheduledFuture,
	currentlyRealTimeFuture,
	startTimeAllCases,
	endTimeAllCases,
	currentlyScheduledCompleted,
	currentlyRealTimeCompleted
} from "./realTimeUtils"

export function currentlyNurseRealTimeOngoing(nurse, sandbox, isTomorrowMode) {
	const currentTime = getCurrentTime(sandbox, isTomorrowMode)
	return nurse?.scheduledStartTime <= currentTime && nurse?.scheduledEndTime > currentTime
}

export function currentlyNurseRealTimeFuture(nurse, sandbox, isTomorrowMode) {
	const currentTime = getCurrentTime(sandbox, isTomorrowMode)
	return nurse?.scheduledStartTime > currentTime
}

export function currentlyNurseLunchBreakOngoing(nurse) {
	return nurse?.actualLunchBreakStart && !nurse?.actualLunchBreakEnd
}

export function currentlyNurseLunchBreakFinished(nurse) {
	return nurse?.actualLunchBreakEnd
}

export function currentlyNurseLunchBreakFuture(nurse) {
	return !nurse?.actualLunchBreakStart
}

export const calculateNurseTotalAppointments = (nurse, patientSchedule) => {
	if (!nurse) return 0
	return getNursePatients(patientSchedule, nurse.nurseId)
		.filter(p => !confirmedCancellationNoShow(p)).length
}

export const calculateTotalAcuity = (nurse, patientSchedule) => {
	if (!nurse) return 0

	const transferredAcuity = patientSchedule
		.filter(p => p.transferredBy === nurse.nurseId)
		.reduce((sum, p) => sum + (confirmedCancellationNoShow(p) ? 0 : p.acuity), 0)

	return getNursePatients(patientSchedule, nurse.nurseId).reduce(
		(sum, p) => sum + (confirmedCancellationNoShow(p) ? 0 : p.acuity),
		transferredAcuity
	)
}

export const calculateTotalPatients = (nurse, patientSchedule) => {
	if (!nurse) return 0
	return getNursePatients(patientSchedule, nurse.nurseId)
		.filter(p => !confirmedCancellationNoShow(p)).length
}

export const calculateNurseAcuity = (
	nurses = [],
	patientSchedule = [],
	patientStartTime,
	patientEndTime,
	sandbox,
	isTomorrowMode
) => {
	if (patientStartTime === undefined || patientEndTime === undefined) {
		patientStartTime = getCurrentTime(sandbox, isTomorrowMode)
		patientEndTime = getCurrentTime(sandbox, isTomorrowMode) + 1
	}

	// Ensure nurses is an array before using map
	return Array.isArray(nurses)
		? nurses.map(nurse => {
				const relevantPatients = getNursePatients(patientSchedule, nurse.nurseId).filter(
					patient => {
						const compareStartTime = startTimeAllCases(
							patient,
							sandbox,
							isTomorrowMode
						)
						const compareEndTime = endTimeAllCases(patient, sandbox, isTomorrowMode)
						return rangesOverlap(
							[compareStartTime, compareEndTime],
							[patientStartTime, patientEndTime]
						)
					}
				)

				const totalAcuity = relevantPatients.reduce((sum, patient) => {
					const isCancelledOrCompleted = confirmedCancellationNoShow(patient) || 
						(sandbox || isTomorrowMode ? currentlyScheduledCompleted(patient, sandbox, isTomorrowMode) : currentlyRealTimeCompleted(patient, sandbox, isTomorrowMode));
					
					return sum + (isCancelledOrCompleted ? 0 : patient.acuity);
				}, 0);

				return {
					...nurse,
					currentAcuity: totalAcuity
				}
			})
		: []
}

// TODO get rid of calculateNurseAvailability and instead use getAvailableNursesForPatient

/**
 * Determines if a nurse is available to take on a new patient based on various constraints
 * including shift times, existing patient loads, and acuity limits.
 *
 * TODO include an option to check if the nurse is eligible based on the acuity table eligability rules
 *
 * @param {Schedule} schedule - Schedule object containing nurseSchedule and patientSchedule
 * @param {Nurse} nurse - Nurse object containing scheduledStartTime, scheduledEndTime, and currentAcuity
 * @param {Patient} patient - Patient object containing patientId and acuity
 *
 *
 * @param {Object} [toggleRules={ MAFN: true, MAFNAT: true }] - Rules toggle object
 * @param {boolean} toggleRules.MAFN - Maximum Acuity For Nurse rule toggle
 * @param {boolean} toggleRules.MAFNAT - Maximum Acuity For Nurse At Time rule toggle
 * @param {number} [MAFN=15] - Maximum Acuity For Nurse (total acuity limit across all patients)
 * @param {number} [MAFNAT=15] - Maximum Acuity For Nurse At Time (concurrent acuity limit)
 * 
 * @param {boolean} sandbox - Whether the operation is in a sandbox environment.
 * @param {Object} logging - The logging object.
 *
 * @returns {boolean} true if the nurse is available to take on the patient, false otherwise
 */
export const isNurseAvailableForPatient = ({
	schedule,
	nurse,
	patient,
	toggleRules = { MAFN: true, MAFNAT: true },
	MAFN = 15,
	MAFNAT = 15,
	sandbox,
	logging,
	isTomorrowMode
}) => {
	if (confirmedCancellationNoShow(patient)) {
		logging.warn("Patient is a cancellation or no show why are you asking nurse is available?")
	}
	// Check if the time period falls outside nurse's shift
	if (
		Math.max(getStartTime(patient, sandbox, isTomorrowMode), getCurrentTime(sandbox, isTomorrowMode)) < nurse.scheduledStartTime ||
		getEndTime(patient, sandbox, isTomorrowMode) > nurse.scheduledEndTime
	) {
		return false
	}

	const hasOverlappingOneToOne = getNursePatients(schedule.patientSchedule, nurse.nurseId).some(
		nursesPatient => {
			if (nursesPatient.acuity !== 5) return false
			return rangesOverlap(
				[getStartTime(nursesPatient, sandbox, isTomorrowMode), getEndTime(nursesPatient, sandbox, isTomorrowMode)],
				[getStartTime(patient, sandbox, isTomorrowMode), getEndTime(patient, sandbox, isTomorrowMode)]
			)
		}
	)

	if (hasOverlappingOneToOne) {
		return false
	}

	// Calculate the total acuity across all assigned patients (MAFN limit)
	const totalAcuity = calculateTotalAcuity(nurse, schedule.patientSchedule)

	if (MAFNAT - nurse.currentAcuity < patient.acuity) return false
	if (MAFN - totalAcuity < patient.acuity) return false
	return true
}

/**
 * Determines which nurses are available to take on a new patient based on various constraints
 * including shift times, existing patient loads, and acuity limits.
 *
 * @param {Schedule} schedule - Schedule object containing nurseSchedule and patientSchedule
 * @param {Patient} patient - Patient object containing patientId and acuity
 * 
 * 
 * @param {Object} [toggleRules={ MAFN: true, MAFNAT: true }] - Rules toggle object
 * @param {boolean} toggleRules.MAFN - Maximum Acuity For Nurse rule toggle

 * @param {boolean} toggleRules.MAFNAT - Maximum Acuity For Nurse At Time rule toggle
 * @param {number} [MAFN=15] - Maximum Acuity For Nurse (total acuity limit across all patients)
 * @param {number} [MAFNAT=15] - Maximum Acuity For Nurse At Time (concurrent acuity limit)
 *
 * @returns {Array<boolean>} Array of boolean values indicating each nurse's availability
 *                          (true = available, false = unavailable)
 *
 * @description
 * A nurse is considered unavailable if any of these conditions are met:
 * 1. The patient's appointment time falls outside the nurse's scheduled shift
 * 2. The nurse has a 1:1 patient (acuity=5) during the new patient's time
 * 3. Adding the new patient would exceed MAFN (total acuity limit)
 * 4. Adding the new patient would exceed MAFNAT (concurrent acuity limit)
 *
 * If no nurses are available based on acuity rules, the function falls back to returning
 * a list of nurses who are at least on shift during the patient's appointment time.
 */
export const calculateNurseAvailability = (
	schedule,
	patient,
	toggleRules = { MAFN: true, MAFNAT: true },
	sandbox,
	isTomorrowMode,
	MAFN = 15,
	MAFNAT = 15,
	logging
) => {
	if (!toggleRules.MAFN) {
		MAFN = 15
	}
	if (!toggleRules.MAFNAT) {
		MAFNAT = 15
	}

	const nurses = schedule.nurseSchedule

	if (!nurses.every(nurse => nurse.hasOwnProperty("currentAcuity"))) {
		logging.warn("One or more nurses are missing the 'currentAcuity' field.")
	}

	const availableNurses = nurses.map(nurse =>
		isNurseAvailableForPatient({
			schedule,
			nurse,
			patient,
			toggleRules,
			MAFN,
			MAFNAT,
			sandbox,
			logging,
			isTomorrowMode
		})
	)

	if (!availableNurses.some(Boolean)) {
		return nurses.map(
			nurse =>
				Math.max(getStartTime(patient, sandbox, isTomorrowMode), getCurrentTime(sandbox, isTomorrowMode)) >= nurse.scheduledStartTime &&
				getEndTime(patient, sandbox, isTomorrowMode) <= nurse.scheduledEndTime
		)
	}

	return availableNurses
}

/**
 * Returns a list of nurse IDs who are available to take on a new patient based on various constraints

 * including shift times, existing patient loads, and acuity limits.
 *
 * @param {Schedule} schedule - Schedule object containing nurseSchedule and patientSchedule
 * @param {Patient} patient - Patient object containing patientId and acuity
 *
 * @param {Object} [toggleRules={ MAFN: true, MAFNAT: true }] - Rules toggle object
 * @param {number} patientEndTime - End time of the new patient's appointment
 * @param {Object} [toggleRules={ MAFN: true, MAFNAT: true }] - Rules toggle object
 * @param {boolean} toggleRules.MAFN - Maximum Acuity For Nurse rule toggle
 * @param {boolean} toggleRules.MAFNAT - Maximum Acuity For Nurse At Time rule toggle
 * @param {number} [MAFN=15] - Maximum Acuity For Nurse (total acuity limit across all patients)
 * @param {number} [MAFNAT=15] - Maximum Acuity For Nurse At Time (concurrent acuity limit)
 * @param {boolean} sandbox - Flag indicating if running in sandbox mode
 * @param {Object} logging - The logging object.
 *
 * @returns {Array<string>} Array of nurse IDs who are available to take the patient
 */
export const getAvailableNursesForPatient = ({
	schedule,
	patient,
	toggleRules = { MAFN: true, MAFNAT: true },
	MAFN = 15,
	MAFNAT = 15,
	logging,
	sandbox,
	isTomorrowMode
}) => {
	const nurses = schedule.nurseSchedule
	const availableNurses = nurses.filter(nurse =>
		isNurseAvailableForPatient({
			schedule,
			nurse,
			patient,
			toggleRules,
			MAFN,
			MAFNAT,
			logging,
			sandbox,
			isTomorrowMode
		})
	)

	return availableNurses.map(nurse => nurse.nurseId)
}

export const sortNursesByAcuity = (
	nurses = [],
	patientSchedule = [],
	sandbox,
	isTomorrowMode,
	current = true
) => {
	// Ensure nurses is an array before processing
	if (!Array.isArray(nurses)) {
		return []
	}

	// Calculate both ongoing and future acuity for each nurse
	const nursesWithAcuityInfo = nurses.map(nurse => {
		// Map to store ongoing acuity at each minute

		// Calculate ongoing acuity
		const ongoingAcuity = calculateNurseAcuity(
			[nurse],
			patientSchedule,
			sandbox,
			isTomorrowMode
		)[0].currentAcuity

		// Calculate future acuity (patients that haven't started yet)
		const futureAcuity = getNursePatients(patientSchedule, nurse.nurseId).reduce(
			(sum, patient) => {
				const isFuture =
					sandbox || isTomorrowMode
						? currentlyScheduledFuture(patient, sandbox, isTomorrowMode)
						: currentlyRealTimeFuture(patient, sandbox, isTomorrowMode)
				return isFuture ? sum + patient.acuity : sum
			},
			0
		)

		return {
			...nurse,
			ongoingAcuity: ongoingAcuity,
			futureAcuity: futureAcuity
		}
	})

	// Sort nurses by future acuity (ascending order)
	if (current) {
		return [...nursesWithAcuityInfo].sort((a, b) => a.ongoingAcuity - b.ongoingAcuity)
	} else {
		return [...nursesWithAcuityInfo].sort((a, b) => a.futureAcuity - b.futureAcuity)
	}
}

/**
 * Composes calculateNurseAcuity + calculateNurseAvailability and returns a
 * sorted, decorated nurse list ready to feed into a dropdown:
 *
 *   - Available nurses first
 *   - Then by ascending currentAcuity
 *   - Then by nurseName (alpha)
 *
 * Each returned nurse carries `currentAcuity` (from acuity calc) and
 * `isAvailable` (from availability calc).
 */
export const getSortedNursesForPatient = ({
	nurseSchedule,
	patientSchedule,
	chairSchedule,
	patient,
	sandbox,
	isTomorrowMode,
	maxAcuityForNurse,
	maxAcuityForNurseAT,
	logging
}) => {
	const patientStart = startTimeAllCases(patient, sandbox, isTomorrowMode)
	const patientEnd = endTimeAllCases(patient, sandbox, isTomorrowMode)

	const nursesWithAcuity = calculateNurseAcuity(
		nurseSchedule,
		patientSchedule,
		patientStart,
		patientEnd,
		sandbox,
		isTomorrowMode
	)

	const nurseAvailability = calculateNurseAvailability(
		{
			patientSchedule,
			nurseSchedule: nursesWithAcuity,
			chairSchedule
		},
		patient,
		{ MAFN: true, MAFNAT: true },
		sandbox,
		isTomorrowMode,
		maxAcuityForNurse,
		maxAcuityForNurseAT,
		logging
	)

	return nursesWithAcuity
		.map((nurse, i) => ({ ...nurse, isAvailable: nurseAvailability[i] }))
		.sort((a, b) => {
			if (a.isAvailable !== b.isAvailable) return b.isAvailable - a.isAvailable
			if (a.currentAcuity !== b.currentAcuity) return a.currentAcuity - b.currentAcuity
			return a.nurseName.localeCompare(b.nurseName)
		})
}

export const calculateFutureNurseAcuity = (
	nurses = [],
	patientSchedule = [],
	sandbox,
	isTomorrowMode
) => {
	// Sum the acuity of patients that have not been started yet
	return Array.isArray(nurses)
		? nurses.map(nurse => {
				const futureAcuity = getNursePatients(patientSchedule, nurse.nurseId).reduce(
					(sum, patient) => {
						const isFuture =
							sandbox || isTomorrowMode
								? currentlyScheduledFuture(patient, sandbox, isTomorrowMode)
								: currentlyRealTimeFuture(patient, sandbox, isTomorrowMode)
						return isFuture ? sum + patient.acuity : sum
					},
					0
				)

				return {
					...nurse,
					futureAcuity
				}
			})
		: []
}
